import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountdownOptions {
  /** 本地时钟与服务端时钟偏差（ms），用于校正倒计时 */
  serverTimeOffset?: number;
  /** 是否启用（false 时暂停但不销毁） */
  enabled?: boolean;
  /** 倒计时归零时回调 */
  onExpire?: () => void;
}

interface UseCountdownResult {
  /** 剩余毫秒 */
  remainingMs: number;
  /** 剩余秒（向下取整） */
  remainingSec: number;
  /** 是否已到期 */
  expired: boolean;
  /** 重设倒计时终点（支持防秒杀延长重算） */
  restart: (endTime: string | number | Date | null | undefined) => void;
}

/**
 * 倒计时 Hook：以目标时间为准，使用本地时钟 + 服务端偏差校正。
 * 支持调用 restart 重算终点（竞价防秒杀延长、订单锁定刷新）。
 */
export function useCountdown(
  endTime: string | number | Date | null | undefined,
  options: UseCountdownOptions = {},
): UseCountdownResult {
  const { serverTimeOffset = 0, enabled = true, onExpire } = options;

  const targetRef = useRef<number | null>(endTime ? new Date(endTime).getTime() : null);
  const offsetRef = useRef(serverTimeOffset);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const [remainingMs, setRemainingMs] = useState<number>(() => {
    const target = targetRef.current;
    if (target === null) return 0;
    return Math.max(0, target - (Date.now() + offsetRef.current));
  });

  const [expired, setExpired] = useState<boolean>(() => {
    const target = targetRef.current;
    return target !== null && target <= Date.now() + offsetRef.current;
  });

  useEffect(() => {
    offsetRef.current = serverTimeOffset;
  }, [serverTimeOffset]);

  useEffect(() => {
    if (!enabled || targetRef.current === null) return;
    const timer = window.setInterval(() => {
      const target = targetRef.current;
      if (target === null) return;
      const remaining = Math.max(0, target - (Date.now() + offsetRef.current));
      setRemainingMs(remaining);
      if (remaining <= 0) {
        setExpired(true);
        window.clearInterval(timer);
        onExpireRef.current?.();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  const restart = useCallback((newEndTime: string | number | Date | null | undefined) => {
    targetRef.current = newEndTime ? new Date(newEndTime).getTime() : null;
    setExpired(targetRef.current === null || targetRef.current <= Date.now() + offsetRef.current);
    setRemainingMs(
      targetRef.current === null
        ? 0
        : Math.max(0, targetRef.current - (Date.now() + offsetRef.current)),
    );
  }, []);

  return {
    remainingMs,
    remainingSec: Math.ceil(remainingMs / 1000),
    expired,
    restart,
  };
}
